import { Box, Typography, Grid, TextField, Button, SvgIcon, useTheme } from '@mui/material';
import { Search } from '@mui/icons-material';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import { useContext, useState, useEffect, FC, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import AllItemsList from './AllItemsList';
import { Item } from '../../../../app/services/api/generated';
import { Section } from '../../../../components/ui/Section';
import { handleSearchFieldKeyDown } from '../../../../utils';

type Props = {
  hasNewItemCreated: number;
  setItemCount: Dispatch<SetStateAction<number | undefined>>;
  itemCount: number | undefined;
  onCreateNew: () => void;
}

const ItemsTab: FC<Props> = ({ hasNewItemCreated, itemCount, setItemCount, onCreateNew }) => {
  const { t } = useTranslation('products');
  const theme = useTheme();
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { getItems } = useNswagClient();
  const { logError } = useLogError();
  const [allItems, setAllItems] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Item>('itemNumber');

  const hasAnyReadPermission = hasPermissionTo(['ItemDetailsRead']) || hasPermissionTo(['ItemHistoryRead']) || hasPermissionTo(['ItemCostsRead']);

  const isAsc = order === 'asc';
  const rowsPerPage = 10;

  const maxPages = () => {
    const safeItemCount = Number.isFinite(itemCount) ? Number(itemCount) : 0;
    return Math.ceil(safeItemCount / rowsPerPage);
  };

  useEffect(() => {
    loadData();
  }, [hasNewItemCreated, page]);

  const loadData = (passedOrder?: 'asc' | 'desc', passedOrderBy?: keyof Item, passedSearchTerm?: string, passedPage?: number) => {
    const orderToUse = passedOrder ?? order;
    const orderByToUse = passedOrderBy ?? orderBy;
    const searchTermToUse = passedSearchTerm ?? searchTerm;
    const pageToUse = passedPage ?? page;

    const pageSkip = rowsPerPage * pageToUse;

    setIsLoading(true);
    if (selectedStore?.storeNumber) {
      getItems(searchTermToUse, selectedStore.storeNumber, undefined, undefined, undefined, undefined, undefined, true, true, false, orderByToUse, orderToUse, pageSkip, rowsPerPage).then((result) => {
        if (result?.data?.items) {
          setAllItems(result.data.items);
          setItemCount(result.data.itemCount ?? 0);
        }
      })
        .catch((error) => {
          logError(error);
        })
        .finally(() => {
          setLastSearchTerm(searchTermToUse);
          setIsLoading(false);
        });
    }
  };

  const handleSort = (orderBy: keyof Item) => {
    const newDirection = isAsc ? 'desc' : 'asc';
    setOrder(newDirection);
    setOrderBy(orderBy);
    loadData(newDirection, orderBy);
  };

  const onSearch = () => {
    setPage(0);
    loadData(undefined, undefined, undefined, 0);
  };

  const onClear = () => {
    setSearchTerm('');
    setPage(0);
    loadData(undefined, undefined, '', 0);
  };

  return (
    <Box sx={{ pt: '6px' }}>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <TextField
            label={t('search')}
            size='small'
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            onKeyDown={e => handleSearchFieldKeyDown(e, onSearch)}
            fullWidth />
        </Grid>
        <Grid item
          xs={6}>
          <Button
            disabled={isLoading}
            fullWidth
            onClick={onClear}
            variant='secondary'
            size='lg'
            sx={{ mr: 4 }}
          >
            {t('clear')}
          </Button>
        </Grid>
        <Grid item
          xs={6}>
          <Button
            disabled={isLoading}
            fullWidth
            onClick={onSearch}
            size='lg'
            startIcon={<SvgIcon><Search /></SvgIcon>}>
            {t('search')}
          </Button>
        </Grid>
        <Grid item
          xs={12}>
          <Section>
            <AllItemsList isLoading={isLoading}
              list={allItems}
              handleSort={handleSort}
              order={order}
              orderBy={orderBy}
              page={page}
              setPage={setPage}
              maxPages={maxPages}
              itemCount={itemCount}
              onCreateNew={onCreateNew}
              onClear={onClear}
              searchTerm={lastSearchTerm}
              hasAnyReadPermission={hasAnyReadPermission}
            />
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ItemsTab;